import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { FaExternalLinkAlt, FaGithub, FaCode, FaSearch, FaStar } from 'react-icons/fa';
import API from '../api/axios';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';

const ProjectsPage = () => {
  const [projects, setProjects] = useState([]);
  const [search, setSearch] = useState('');
  const [filter, setFilter] = useState('all');

  useEffect(() => { API.get('/projects').then(r => setProjects(r.data)).catch(console.error); }, []);

  const defaultProjects = [
    { _id: '1', title: 'E-Commerce Platform', description: 'Full-featured online store with product management, cart, checkout, and payment integration. Built with MERN stack.', technologies: ['React', 'Node.js', 'MongoDB', 'Stripe'], liveUrl: '#', githubUrl: '#', featured: true },
    { _id: '2', title: 'Social Media Dashboard', description: 'Real-time social media analytics dashboard with charts, user management, and notification system.', technologies: ['React', 'Express', 'Socket.io', 'Chart.js'], liveUrl: '#', githubUrl: '#', featured: true },
    { _id: '3', title: 'WordPress Business Site', description: 'Professional business website built with WordPress, Elementor, and custom theme. SEO optimized.', technologies: ['WordPress', 'Elementor', 'CSS', 'SEO'], liveUrl: '#', githubUrl: '#' },
    { _id: '4', title: 'Restaurant Booking App', description: 'Online restaurant with menu, table booking, and order management system.', technologies: ['React', 'Node.js', 'MongoDB'], liveUrl: '#', githubUrl: '#' },
    { _id: '5', title: 'Portfolio & Blog', description: 'Modern portfolio website with blog, admin panel, and dynamic content management.', technologies: ['React', 'Express', 'MongoDB'], liveUrl: '#', githubUrl: '#', featured: true },
    { _id: '6', title: 'SEO Audit Tool', description: 'Web-based SEO audit tool that analyzes websites and provides optimization recommendations.', technologies: ['Node.js', 'React', 'Cheerio'], liveUrl: '#', githubUrl: '#' },
  ];

  const displayProjects = projects.length > 0 ? projects : defaultProjects;

  // Get unique technologies
  const allTechs = [...new Set(displayProjects.flatMap(p => p.technologies || []))];

  const filtered = displayProjects.filter(p => {
    const matchSearch = p.title.toLowerCase().includes(search.toLowerCase()) || p.description?.toLowerCase().includes(search.toLowerCase());
    const matchFilter = filter === 'all' || (p.technologies || []).includes(filter);
    return matchSearch && matchFilter;
  });

  const gradients = [
    'linear-gradient(135deg, #6c63ff, #3b3994)', 'linear-gradient(135deg, #ff6584, #d63384)',
    'linear-gradient(135deg, #00d2ff, #0080b3)', 'linear-gradient(135deg, #ffc371, #ff5f6d)',
    'linear-gradient(135deg, #4caf50, #2e7d32)', 'linear-gradient(135deg, #9c27b0, #6a1b9a)',
  ];

  return (
    <>
      <Navbar />
      <div style={{ paddingTop: '80px' }}>
        {/* Banner */}
        <section style={{ background: 'var(--gradient-hero)', padding: '80px 0 60px', textAlign: 'center' }}>
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}>
            <h1 style={{ fontSize: '2.5rem', marginBottom: '10px' }}>My <span style={{ background: 'var(--gradient-1)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent' }}>Projects</span></h1>
            <p style={{ color: 'var(--text-secondary)', fontSize: '1.1rem' }}>Showcasing my work and creative solutions</p>
          </motion.div>
        </section>

        <section className="section" style={{ background: 'var(--bg-dark)' }}>
          <div className="container">
            {/* Search & Filter */}
            <div style={{ display: 'flex', gap: '20px', marginBottom: '40px', flexWrap: 'wrap', justifyContent: 'center' }}>
              <div style={{ position: 'relative', flex: 1, maxWidth: '400px' }}>
                <FaSearch style={{ position: 'absolute', left: '15px', top: '50%', transform: 'translateY(-50%)', color: 'var(--text-muted)' }} />
                <input type="text" placeholder="Search projects..." value={search} onChange={e => setSearch(e.target.value)}
                  style={{ width: '100%', padding: '12px 18px 12px 40px', background: 'var(--bg-card)', border: '1px solid var(--border)', borderRadius: '12px', color: 'var(--text-primary)', fontFamily: 'Poppins' }} />
              </div>
              <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap' }}>
                <button onClick={() => setFilter('all')} className={`btn btn-small ${filter === 'all' ? 'btn-primary' : 'btn-outline'}`}>All</button>
                {allTechs.slice(0, 6).map(tech => (
                  <button key={tech} onClick={() => setFilter(tech)} className={`btn btn-small ${filter === tech ? 'btn-primary' : 'btn-outline'}`}>{tech}</button>
                ))}
              </div>
            </div>

            {/* Projects Grid */}
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(350px, 1fr))', gap: '30px' }}>
              {filtered.map((project, idx) => (
                <motion.div key={project._id} style={{ background: 'var(--bg-card)', borderRadius: '20px', overflow: 'hidden', border: '1px solid var(--border)', transition: 'all 0.3s' }}
                  initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}
                  transition={{ duration: 0.4, delay: idx * 0.1 }} whileHover={{ y: -8, boxShadow: '0 0 30px rgba(108,99,255,0.3)' }}>
                  {/* Image */}
                  <div style={{ height: '200px', background: gradients[idx % gradients.length], display: 'flex', alignItems: 'center', justifyContent: 'center', position: 'relative' }}>
                    {project.featured && <div style={{ position: 'absolute', top: '12px', right: '12px', padding: '4px 12px', background: 'rgba(0,0,0,0.5)', borderRadius: '20px', fontSize: '0.75rem', display: 'flex', alignItems: 'center', gap: '4px', color: '#ffc107' }}><FaStar /> Featured</div>}
                    <FaCode style={{ fontSize: '3rem', color: 'rgba(255,255,255,0.9)', zIndex: 1 }} />
                    <div style={{ position: 'absolute', inset: 0, background: 'linear-gradient(to bottom, transparent 50%, var(--bg-card))' }} />
                  </div>

                  <div style={{ padding: '25px' }}>
                    <h3 style={{ fontSize: '1.3rem', marginBottom: '10px' }}>{project.title}</h3>
                    <p style={{ color: 'var(--text-secondary)', fontSize: '0.9rem', lineHeight: '1.6', marginBottom: '15px' }}>{project.description}</p>

                    <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', marginBottom: '15px' }}>
                      {project.technologies?.map((tech, i) => (
                        <span key={i} style={{ padding: '4px 12px', background: 'rgba(108,99,255,0.15)', color: 'var(--primary)', borderRadius: '20px', fontSize: '0.8rem', fontWeight: '500' }}>{tech}</span>
                      ))}
                    </div>

                    <div style={{ display: 'flex', gap: '12px' }}>
                      {project.liveUrl && project.liveUrl !== '#' && (
                        <a href={project.liveUrl} target="_blank" rel="noreferrer" className="btn btn-primary btn-small">
                          <FaExternalLinkAlt /> Live Demo
                        </a>
                      )}
                      {project.githubUrl && project.githubUrl !== '#' && (
                        <a href={project.githubUrl} target="_blank" rel="noreferrer" className="btn btn-outline btn-small">
                          <FaGithub /> Code
                        </a>
                      )}
                      {(!project.liveUrl || project.liveUrl === '#') && (!project.githubUrl || project.githubUrl === '#') && (
                        <span style={{ color: 'var(--text-muted)', fontSize: '0.85rem' }}>Links coming soon</span>
                      )}
                    </div>
                  </div>
                </motion.div>
              ))}
            </div>

            {filtered.length === 0 && <p style={{ textAlign: 'center', color: 'var(--text-muted)', padding: '40px' }}>No projects found</p>}
          </div>
        </section>
      </div>
      <Footer />
    </>
  );
};

export default ProjectsPage;
